import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const useCalvielleTransition = Boolean(this.closest('.calvielle-product-studio'));
    if (useCalvielleTransition) {
      this.dataset.variantTransition = 'preparing';
      this.setAttribute('aria-busy', 'true');
    }

    event.promise
      .then(async ({ detail }) => {
        if (!detail?.html) {
          this.#clearVariantTransitionState();
          return;
        }

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) {
          this.#clearVariantTransitionState();
          return;
        }

        if (!useCalvielleTransition) {
          this.replaceWith(newMediaGallery);
          return;
        }

        await this.#replaceWithVariantTransition(newMediaGallery);
      })
      .catch((error) => {
        this.#clearVariantTransitionState();
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Replaces the Calvielle PDP gallery without flashing an unloaded variant image.
   * Native document transitions provide a true cross-fade; the attribute-driven
   * fallback preserves the same restrained entrance in other browsers.
   *
   * @param {Element} newMediaGallery
   */
  async #replaceWithVariantTransition(newMediaGallery) {
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    await this.#prepareFirstImage(newMediaGallery);

    if (!this.isConnected) return;

    const swapGallery = () => {
      newMediaGallery.removeAttribute('aria-busy');
      this.replaceWith(newMediaGallery);
    };

    if (prefersReducedMotion) {
      swapGallery();
      return;
    }

    if (typeof document.startViewTransition === 'function') {
      try {
        const transition = document.startViewTransition(swapGallery);
        await transition.updateCallbackDone;
        return;
      } catch (error) {
        if (!this.isConnected) return;
        console.warn('[media-gallery] View transition unavailable, using fallback:', error);
      }
    }

    newMediaGallery.dataset.variantTransition = 'entering';
    swapGallery();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => newMediaGallery.removeAttribute('data-variant-transition'));
    });
  }

  /**
   * Warms the first visible image while the current variant remains on screen.
   * The cap prevents a slow asset from blocking the selection indefinitely.
   *
   * @param {Element} newMediaGallery
   */
  async #prepareFirstImage(newMediaGallery) {
    const image = newMediaGallery.querySelector('.product-media-container:not([hidden]) img, img');
    if (!(image instanceof HTMLImageElement) || image.complete) return;

    image.loading = 'eager';
    image.fetchPriority = 'high';

    await Promise.race([
      image.decode().catch(() => undefined),
      new Promise((resolve) => window.setTimeout(resolve, 1200)),
    ]);
  }

  #clearVariantTransitionState() {
    this.removeAttribute('data-variant-transition');
    this.removeAttribute('aria-busy');
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
